import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import type { EventDetails, EventSummary } from './events.types';

interface EventDataDrawerProps {
  details: EventDetails | null;
  error: string | null;
  event: EventSummary | null;
  loading: boolean;
  onClose: () => void;
  onRetry: () => void;
}

export const formatEventDataLabel = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const EventDataValue = ({ value }: { value: unknown }) => {
  if (value === null || value === undefined || value === '') {
    return <Typography color="text.secondary">--</Typography>;
  }

  if (typeof value === 'object') {
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          overflowX: 'auto',
          borderRadius: 1,
          bgcolor: 'grey.100',
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {JSON.stringify(value, null, 2)}
      </Box>
    );
  }

  return <Typography sx={{ wordBreak: 'break-word' }}>{String(value)}</Typography>;
};

export default function EventDataDrawer({
  details,
  error,
  event,
  loading,
  onClose,
  onRetry,
}: EventDataDrawerProps) {
  const theme = useTheme();
  const matchingDetails = details?.event_id === event?.event_id ? details : null;
  const displayedEvent = matchingDetails || event;
  const eventDetailEntries = Object.entries(matchingDetails || {}).filter(
    ([key]) => !['event_data', 'event_id', 'event_name', 'pk', 'sk'].includes(key),
  );
  const eventDataEntries = Object.entries(matchingDetails?.event_data || {}).filter(
    ([key]) => key !== 'source',
  );
  const shouldShowDetails = !loading && !error && matchingDetails !== null;
  const shouldShowEmptyDetails = !loading && !error && matchingDetails === null;
  const hasEventData = eventDataEntries.length > 0;

  return (
    <Drawer
      anchor="right"
      open={Boolean(event)}
      onClose={onClose}
      sx={{ zIndex: theme.zIndex.drawer + 2 }}
      PaperProps={{
        role: 'dialog',
        'aria-labelledby': 'event-details-title',
        sx: {
          width: { xs: '100%', sm: 440 },
          maxWidth: '100vw',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box
          sx={{
            flexShrink: 0,
            px: 2.5,
            pt: 2.5,
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={2}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography id="event-details-title" component="h2" variant="h6" color="primary">
                Event Details
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {displayedEvent?.event_name || '--'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {displayedEvent?.event_id || '--'}
              </Typography>
            </Box>
            <IconButton aria-label="Close event details" onClick={onClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ mt: 2 }} />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.5, py: 2 }}>
        {loading && (
          <Box
            role="status"
            aria-label="Loading event details"
            sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            action={(
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            )}
          >
            {error}
          </Alert>
        )}

        {shouldShowDetails && (
          <Stack spacing={2.5}>
            {eventDetailEntries.length > 0 && (
              <Box>
                <Typography
                  component="h3"
                  variant="subtitle2"
                  color="text.primary"
                  sx={{ mb: 1.5, fontWeight: 700 }}
                >
                  Event Information
                </Typography>
                <Stack spacing={2}>
                  {eventDetailEntries.map(([key, value]) => (
                    <Box key={key}>
                      <Typography
                        component="h4"
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}
                      >
                        {formatEventDataLabel(key)}
                      </Typography>
                      <EventDataValue value={value} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            <Divider />

            <Box>
              <Typography
                component="h3"
                variant="subtitle2"
                color="text.primary"
                sx={{ mb: 1.5, fontWeight: 700 }}
              >
                Event Data
              </Typography>
              {hasEventData && (
                <Stack spacing={2}>
                  {eventDataEntries.map(([key, value]) => (
                    <Box key={key}>
                      <Typography
                        component="h4"
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}
                      >
                        {formatEventDataLabel(key)}
                      </Typography>
                      <EventDataValue value={value} />
                    </Box>
                  ))}
                </Stack>
              )}
              {!hasEventData && (
                <Typography color="text.secondary">No event data available.</Typography>
              )}
            </Box>
          </Stack>
        )}

          {shouldShowEmptyDetails && (
            <Typography color="text.secondary">No event details available.</Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
